import React, {useState, useEffect, useContext} from 'react';
import './DurationPicker.scss';
import {ThemeProvider} from "@mui/material/styles";
import {InputTheme} from "../InputTheme";
import {FormInput} from "../../Form/Input/FormInput";
import {SelectBox} from "../SelectBox/SelectBox";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {DurationUnits, DEFAULT_DURATION_VALUES, MAX_DURATION_VALUES} from "../../SignDoc/SignDocLib";

/**
 * Duration picker component for selecting time duration with value and unit.
 * Allows users to specify a duration (e.g., "60 days", "3 months", "1 year")
 * for assignment deadlines and completion timeframes.
 *
 * @param {object} props - Component props
 * @param {object|null} props.value - Current duration: {value: number, unit: string} or null
 * @param {number} props.value.value - Numeric duration value (positive integer)
 * @param {string} props.value.unit - Duration unit: 'days', 'months', or 'years'
 * @param {function} props.onChange - Callback when duration changes: (duration) => void
 * @param {string} [props.label] - Label text for the picker (uses translation if not provided)
 * @param {boolean} [props.disabled=false] - Whether inputs are disabled
 * @param {string} [props.error] - Error message to display
 * @param {string} [props.dataTestId] - Test ID prefix for testing purposes
 * @param {string} [props.translationPrefix='durationPicker'] - Translation key prefix for unit labels
 * @param {Array<string>} [props.allowedUnits] - Array of allowed units (e.g., ['DAYS'] to show only days)
 * @returns {JSX.Element}
 *
 * @example
 * <DurationPicker
 *   value={{value: 60, unit: 'days'}}
 *   onChange={(duration) => console.log(duration)}
 *   label="Duration to complete"
 * />
 *
 * @example
 * // Using custom translation prefix
 * <DurationPicker
 *   value={{value: 60, unit: 'days'}}
 *   onChange={(duration) => console.log(duration)}
 *   label="Expiry Duration"
 *   translationPrefix="certifications.expiryDuration.units"
 * />
 *
 * @example
 * // Restricting to days only
 * <DurationPicker
 *   value={{value: 60, unit: 'days'}}
 *   onChange={(duration) => console.log(duration)}
 *   label="Days to complete"
 *   allowedUnits={['days']}
 * />
 */
export const DurationPicker = ({value, onChange, label, disabled = false, error, allowedUnits = null}) => {
    const {i18n} = useContext(I18nReactContext);

    const [localValue, setLocalValue] = useState(value?.value || DEFAULT_DURATION_VALUES[value?.unit || DurationUnits.DAYS]);
    const [localUnit, setLocalUnit] = useState(value?.unit || DurationUnits.DAYS);

    // Sync local state with prop changes
    useEffect(() => {
        setLocalValue(value?.value || DEFAULT_DURATION_VALUES[value?.unit || DurationUnits.DAYS]);
        setLocalUnit(value?.unit || DurationUnits.DAYS);
    }, [value?.value, value?.unit]);

    /**
     * Handles changes to the duration value input
     * @param {Event} e - Input change event
     */
    const handleValueChange = (e) => {
        const newValue = e.target.value;

        // Allow empty string for clearing
        if (newValue === '') {
            setLocalValue(null);
            onChange({value: null, unit: localUnit || DurationUnits.DAYS});
            return;
        }

        // Parse as integer
        const numValue = parseInt(newValue, 10);

        // Validate positive integer and maximum value
        if (isNaN(numValue) || numValue <= 0) {
            return; // Reject invalid values
        }

        // Enforce maximum value for current unit
        const maxValue = MAX_DURATION_VALUES[localUnit];
        if (numValue > maxValue) {
            return; // Reject values exceeding max
        }

        setLocalValue(numValue);
        onChange({value: numValue, unit: localUnit});
    };

    /**
     * Handles changes to the duration unit dropdown
     * When unit changes, set default value for that unit
     * @param {Event} e - Select change event
     */
    const handleUnitChange = (e) => {
        const newUnit = e.target.value;
        setLocalUnit(newUnit);

        // Get default value for the new unit
        const defaultValue = DEFAULT_DURATION_VALUES[newUnit];

        // Always set the default value when unit changes
        setLocalValue(defaultValue);
        onChange({value: defaultValue, unit: newUnit});
    };

    // Build unit options, filtering by allowedUnits if provided
    const allUnitOptions = [
        {value: DurationUnits.MINUTES, textKey: `durationPicker.${DurationUnits.MINUTES}`},
        {value: DurationUnits.DAYS, textKey: `durationPicker.${DurationUnits.DAYS}`},
        {value: DurationUnits.MONTHS, textKey: `durationPicker.${DurationUnits.MONTHS}`},
        {value: DurationUnits.YEARS, textKey: `durationPicker.${DurationUnits.YEARS}`}
    ];

    const unitOptions = allowedUnits
        ? allUnitOptions.filter(option => allowedUnits.includes(option.value))
        : allUnitOptions;

    return (
        <ThemeProvider theme={InputTheme}>
            <div className="DurationPicker">
                {label && (
                    <div className="DurationPicker__label">
                        {label}
                    </div>
                )}
                <div className="DurationPicker__inputs">
                    <FormInput
                        type="number"
                        value={localValue}
                        onChange={handleValueChange}
                        label={i18n.translate('durationPicker.valueLabel') || ''}
                        placeholder={i18n.translate('durationPicker.valuePlaceholder') || ''}
                        disabled={disabled}
                        error={!!error}
                        className="DurationPicker__value-input"
                        inputProps={{
                            min: 1,
                            max: MAX_DURATION_VALUES[localUnit],
                        }}
                    />
                    <SelectBox
                        className="DurationPicker__unit-select"
                        label={i18n.translate('durationPicker.unitPlaceholder') || ''}
                        value={localUnit}
                        onChange={handleUnitChange}
                        options={unitOptions}
                        disabled={disabled || unitOptions.length === 1}
                    />
                </div>
                {error && (
                    <div className="DurationPicker__error" role="alert">
                        {error}
                    </div>
                )}
            </div>
        </ThemeProvider>
    );
};
